// @flow

import type { OperationComponent } from "@apollo/client";
import { get, cloneDeep } from "lodash";
import type { RaceWagerType } from "@tvg/types/Race";
import type { WagerProfile } from "@tvg/types/UserFDR";
import type { RaceCardProps } from "@fdr/types/ProgramPage";
import { WagerTypesGroup, WagerTypes } from "@fdr/types/WagerTypes";

type ApolloProps = {
  wagerProfile: WagerProfile
};

const QUERY_VARIABLES = {
  wagerProfile: "FDR-Generic"
};

let isWinBetGroup = false;
let descriptions = {};

const getLegGroup = (wagerType) => {
  switch (wagerType) {
    case WagerTypes.WinPlace:
      return 2;
    case WagerTypes.WinShow:
    case WagerTypes.WinPlaceShow:
      return 3;
    default:
      return 1;
  }
};

const setGroupWagerTypes = (wagerTypesList, wagerGroupList): RaceWagerType[] =>
  wagerTypesList.map((wagerType) => {
    const wagerTypeGroupped = { ...wagerType };
    const specialGroup =
      wagerType.group.id === WagerTypes.Win &&
      wagerType.type.id !== WagerTypes.Win;

    if (!wagerTypeGroupped.types) {
      wagerTypeGroupped.specialGroup = specialGroup;
      wagerTypeGroupped.types = specialGroup ? wagerGroupList : [];
    }

    return wagerTypeGroupped;
  });

const groupWinPlaceShow = (newWagerType, wagerTypeList) => {
  let foundGroupIndex = -1;
  const filteredList = wagerTypeList;
  const filteredWagerType = cloneDeep(newWagerType);

  if (
    filteredWagerType.group.id === WagerTypes.Place ||
    filteredWagerType.group.id === WagerTypes.Show
  ) {
    return wagerTypeList;
  }
  if (filteredWagerType.type.id === WagerTypes.Win) {
    return [...wagerTypeList, filteredWagerType];
  }

  filteredWagerType.columnCount = getLegGroup(filteredWagerType.type.id);
  filteredWagerType.type.description =
    filteredWagerType.type.description ||
    get(
      descriptions,
      filteredWagerType.type.code,
      filteredWagerType.group.description
    );
  foundGroupIndex = filteredList.findIndex(
    (wagerTypeFiltered) =>
      wagerTypeFiltered.type.id === WagerTypes.WinPlace ||
      wagerTypeFiltered.type.id === WagerTypes.WinShow ||
      wagerTypeFiltered.type.id === WagerTypes.WinPlaceShow
  );

  filteredWagerType.type.descriptionInternational =
    filteredWagerType.type.id === WagerTypes.WinPlace ||
    filteredWagerType.type.id === WagerTypes.WinShow
      ? get(descriptions, "INT", "")
      : "";

  if (foundGroupIndex >= 0) {
    filteredList[foundGroupIndex] = filteredWagerType;
  } else {
    return [...filteredList, filteredWagerType];
  }

  return filteredList;
};

const groupByWagerType = (wagerTypes) => {
  let groupedWagerTypes = [];
  const winGroup = [];

  if (wagerTypes && wagerTypes.length > 0) {
    groupedWagerTypes = wagerTypes.reduce((arrayFiltered, wagerType) => {
      const foundEqual = arrayFiltered.find(
        (item) => item.group.id === wagerType.group.id
      );

      if (
        isWinBetGroup &&
        WagerTypesGroup.WinPlaceShow.includes(wagerType.group.id)
      ) {
        winGroup.push(cloneDeep(wagerType));
        return groupWinPlaceShow(wagerType, arrayFiltered);
      }
      if (!foundEqual) {
        return [...arrayFiltered, wagerType];
      }

      return arrayFiltered;
    }, []);
  }

  return isWinBetGroup
    ? setGroupWagerTypes(groupedWagerTypes, winGroup)
    : groupedWagerTypes;
};

export default {
  skip: (props: RaceCardProps) => get(props, "race.raceNumber", 0) === 0,
  options: (props: ApolloProps) => {
    const variables = {
      ...QUERY_VARIABLES,
      wagerProfile: get(props, "wagerProfile") || "FDR-Generic",
      raceNumber: get(props, "race.raceNumber"),
      trackAbbr: get(props, "race.track.trackCode")
    };

    isWinBetGroup = get(props, "isWinBetGroup", false);
    descriptions = get(props, `descriptionsWinBetGroup`, {});

    return {
      fetchPolicy: "cache-first",
      ssr: false,
      variables
    };
  },
  props: (result: OperationComponent<Response>) => ({
    wagerTypes: groupByWagerType(get(result, "data.race.wagerTypes")),
    location: get(result, "data.race.location"),
    isLoading: result.data.loading
  })
};
